import {
  CoverageGapEntry,
  FilingCalendarPreviewData,
  FilingPreviewEntry,
} from '@commenda/api/new-sales-tax/filing-calendar/types';
import { BadgeProps } from '@commenda/kakashi';
import { SalesTaxFilingFrequencyEnum } from '@commenda/prisma';

export type {
  CoverageGapEntry,
  FilingCalendarGenerateData,
  FilingCalendarGenerateResponse,
  FilingCalendarPreviewData,
  FilingCalendarPreviewResponse,
  FilingPreviewEntry,
} from '@commenda/api/new-sales-tax/filing-calendar/types';

export enum CalendarColumnType {
  NEW_CALENDAR = 'NEW_CALENDAR',
  OLD_CALENDAR = 'OLD_CALENDAR',
}

export enum AlertVariant {
  INFO = 'info',
  WARNING = 'warning',
  ERROR = 'error',
  SUCCESS = 'success',
}

export interface IndirectTaxFilingCalendarPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  corporationId: number;
  initialTaxPeriodStart?: MonthYearValue;
  onSuccess?: () => void;
}

export interface FilingCalendarPreviewFormProps {
  state: string;
  onStateChange: (state: string) => void;
  taxPeriodStart: MonthYearValue | null;
  onTaxPeriodStartChange: (value: MonthYearValue | null) => void;
  taxPeriodEnd: MonthYearValue | null;
  onTaxPeriodEndChange: (value: MonthYearValue | null) => void;
  filingFrequency: SalesTaxFilingFrequencyEnum | null;
  onFilingFrequencyChange: (
    frequency: SalesTaxFilingFrequencyEnum | null
  ) => void;
  validationErrors: CalendarPreviewValidationError[];
  isLoading?: boolean;
}

export interface FilingCalendarPreviewContentProps {
  previewData: FilingCalendarPreviewData | null;
  isLoading: boolean;
  showOldCalendar: boolean;
  taxPeriodStart?: MonthYearValue;
  taxPeriodEnd?: MonthYearValue | null;
  showProposedCalendar?: boolean;
  /** Called after a filing is deleted so the preview can be refetched. */
  onFilingDeleted?: () => void;
  corporationId?: number;
}

export interface PositionedFiling {
  filing: FilingPreviewEntry;
  startMonthIndex: number;
  rowSpan: number;
  /** 0-based lane within the column. Longer/earlier filings take the back
   *  lane (0); shorter/later filings that time-overlap land on higher lanes. */
  laneIndex: number;
  /** Number of lanes in this filing's transitive overlap cluster. The column
   *  is split into `clusterLaneCount` equal-width slots; this filing renders
   *  in the `laneIndex`-th slot. */
  clusterLaneCount: number;
}

export interface CalendarPreviewTableProps {
  months: MonthData[];
  newCalendarFilings: FilingPreviewEntry[];
  oldCalendarFilings?: FilingPreviewEntry[];
  showOldCalendar: boolean;
  coverageGaps?: CoverageGapEntry[];
  startMarkerMonth?: number;
  taxPeriodStart?: MonthYearValue;
  taxPeriodEnd?: MonthYearValue | null;
  onYearInView?: (year: number) => void;
  /** When set, existing filing cards show a delete icon; on delete, this is called to refetch preview. */
  onFilingDeleted?: () => void;
  corporationId?: number;
}

export interface CalendarMonthRowProps {
  month: MonthData;
  newCalendarFilings: FilingPreviewEntry[];
  oldCalendarFilings?: FilingPreviewEntry[];
  showOldCalendar: boolean;
  coverageGap?: CoverageGapEntry;
  showStartMarker?: boolean;
}

export interface FilingInfoCardProps {
  filing: FilingPreviewEntry;
  variant?: 'new' | 'existing' | 'to-delete';
  isCompact?: boolean;
  rowSpan?: number;
  /** When set with corporationId and variant is existing, shows delete icon and allows deleting this filing. */
  onDelete?: (filing: FilingPreviewEntry) => void;
  corporationId?: number;
}

export interface SkippedFilingCardProps {
  gap: CoverageGapEntry;
  onAdjustClick?: () => void;
}

export interface CoverageGapAlertProps {
  gaps: CoverageGapEntry[];
}

export interface FiledConflictAlertProps {
  conflictingFilings: FilingPreviewEntry[];
}

export interface OverlapWarningAlertProps {
  message: string;
  onAdjustPeriod?: () => void;
}

export interface ValidationErrorAlertProps {
  errors: string[];
}

export interface TaxPeriodMonthPickerProps {
  value: MonthYearValue | null;
  onChange: (value: MonthYearValue | null) => void;
  label: string;
  placeholder?: string;
  minDate?: MonthYearValue;
  maxDate?: MonthYearValue;
  disabled?: boolean;
  error?: string;
}

export interface StateSelectorProps {
  value: string;
  onChange: (state: string) => void;
  disabled?: boolean;
  error?: string;
  registrations?: Array<{ state: string }>;
}

export interface FilingFrequencySelectorProps {
  value: SalesTaxFilingFrequencyEnum | null;
  onChange: (frequency: SalesTaxFilingFrequencyEnum | null) => void;
  disabled?: boolean;
  error?: string;
  availableFrequencies: SalesTaxFilingFrequencyEnum[];
}

export interface MonthYearValue {
  month: number; // 1-12
  year: number;
}

export interface MonthData {
  index: number; // 0-11
  name: string; // "Jan", "Feb", etc.
  fullName: string; // "January", "February", etc.
  year: number;
}

export interface CalendarPreviewValidationError {
  field:
    | 'state'
    | 'taxPeriodStart'
    | 'taxPeriodEnd'
    | 'filingFrequency'
    | 'general';
  message: string;
}

export interface CalendarPreviewRequest {
  corporation_id: number;
  state: string;
  filing_frequency: SalesTaxFilingFrequencyEnum;
  tax_period_start_date: string; // YYYY-MM-DD
  tax_period_end_date?: string; // YYYY-MM-DD (optional)
}

export interface CalendarGenerateRequest extends CalendarPreviewRequest {
  confirm: boolean;
}

export interface FilingStatusConfig {
  label: string;
  badgeVariant: BadgeProps['variant'];
  bgColor: string;
  borderColor: string;
  textColor: string;
  dotColor: string;
}

export interface FilingFrequencyConfig {
  label: string;
  value: SalesTaxFilingFrequencyEnum;
  minMonths: number; // Minimum months required for this frequency
}
